import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import JaceAthleteForm from "./JaceAthleteForm";
import { JACE, STATUS_COLORS, TYPE_LABELS, fmtDateNL } from "./shared";

export default function JaceAthleteDetail() {
  const { athleteId } = useParams();
  const nav = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [history, setHistory] = useState(null);
  const [form, setForm] = useState(false);

  const load = () => {
    api.get(`/jace/athletes/${athleteId}`).then(({ data }) => setAthlete(data)).catch(() => setAthlete(false));
    api.get(`/jace/athletes/${athleteId}/history`).then(({ data }) => setHistory(data)).catch(() => setHistory({ sessions: [], feedback: [] }));
  };
  useEffect(load, [athleteId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (athlete === null) return <JaceLayout><div className="chip text-[#A3A3A3]">Laden…</div></JaceLayout>;
  if (athlete === false) return <JaceLayout><div className="chip text-[#F87171]" data-testid="jace-athlete-not-found">Atleet niet gevonden.</div></JaceLayout>;

  return (
    <JaceLayout>
      <div data-testid="jace-athlete-detail">
        <button onClick={() => nav("/jace/athletes")} className="chip text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5 mb-6" data-testid="jace-athlete-back"><ArrowLeft size={12}/> ATHLETES</button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="section-tag section-tag-dark mb-3">// ATHLETE PROFIEL {athlete.archived_at ? "· GEARCHIVEERD" : ""}</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl" data-testid="jace-athlete-name">{athlete.name}.</h2>
            <div className="chip text-[#A3A3A3] mt-3" data-testid="jace-athlete-meta">
              {athlete.dob ? `GEB. ${athlete.dob}` : "GEEN GEBOORTEDATUM"}{athlete.email ? ` · ${athlete.email}` : ""}{athlete.phone ? ` · ${athlete.phone}` : ""}
            </div>
            {athlete.member_email && <div className="chip mt-2 px-2 py-1 border inline-block" style={{ borderColor: JACE, color: JACE }} data-testid="jace-athlete-member-link">GEKOPPELD LID: {athlete.member_email}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setForm(true)} className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-athlete-edit-btn"><Pencil size={13}/> EDIT</button>
            <Link to={`/jace/feedback/new?athlete=${athlete.id}`} className="chip px-4 py-2.5 inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-athlete-new-feedback"><Plus size={13}/> NEW FEEDBACK</Link>
          </div>
        </div>

        {athlete.notes && (
          <div className="border border-[#F5F3F0]/10 p-5 mb-8" data-testid="jace-athlete-notes">
            <div className="chip text-[#A3A3A3] mb-2">// PRIVÉ NOTITIES</div>
            <p className="text-sm text-[#F5F3F0] whitespace-pre-wrap">{athlete.notes}</p>
          </div>
        )}

        {athlete.groups?.length > 0 && (
          <div className="mb-8" data-testid="jace-athlete-groups">
            <div className="chip text-[#A3A3A3] mb-2">// GROEPEN</div>
            <div className="flex flex-wrap gap-2">
              {athlete.groups.map((g) => <Link key={g.id} to="/jace/groups" className="chip px-2 py-1 border border-[#F5F3F0]/25 text-[#F5F3F0]" data-testid={`jace-athlete-group-${g.id}`}>{g.name}</Link>)}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section data-testid="jace-athlete-sessions">
            <div className="section-tag section-tag-dark mb-4">// TRAININGS HISTORIE</div>
            {!history ? <div className="chip text-[#A3A3A3]">Laden…</div> : history.sessions.length === 0 ? (
              <div className="border border-[#F5F3F0]/10 p-6 chip text-[#4A4A4A]">Nog geen sessies gekoppeld aan deze atleet.</div>
            ) : (
              <div className="line-t line-dark">
                {history.sessions.map((s) => (
                  <button key={s.session_id} onClick={() => nav(`/jace/sessions/${s.session_id}`)} className="w-full text-left line-b line-dark py-3 flex items-center justify-between gap-3" data-testid={`jace-athlete-session-${s.session_id}`}>
                    <div>
                      <div className="font-display uppercase">{s.title}</div>
                      <div className="chip text-[#A3A3A3] mt-1">{s.session_id} · {TYPE_LABELS[s.type] || s.type} · {s.date ? fmtDateNL(s.date) : "—"}</div>
                    </div>
                    <span className="chip px-2 py-1 border" style={{ color: STATUS_COLORS[s.status] || "#A3A3A3", borderColor: "currentColor" }}>{s.status}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section data-testid="jace-athlete-feedback">
            <div className="section-tag section-tag-dark mb-4">// FEEDBACK HISTORIE</div>
            {!history ? <div className="chip text-[#A3A3A3]">Laden…</div> : history.feedback.length === 0 ? (
              <div className="border border-[#F5F3F0]/10 p-6 chip text-[#4A4A4A]">Nog geen feedback voor deze atleet.</div>
            ) : (
              <div className="line-t line-dark">
                {history.feedback.map((f) => (
                  <button key={f.id} onClick={() => nav(`/jace/feedback/${f.id}`)} className="w-full text-left line-b line-dark py-3 flex items-center justify-between gap-3" data-testid={`jace-athlete-feedback-${f.id}`}>
                    <div>
                      <div className="font-display uppercase inline-flex items-center gap-2"><FileText size={13} style={{ color: JACE }}/> {f.session_title || "Handmatige evaluatie"}</div>
                      <div className="chip text-[#A3A3A3] mt-1">{f.training_date ? fmtDateNL(f.training_date) : "geen datum"}</div>
                    </div>
                    <span className="chip px-2 py-1 border" style={{ color: f.status === "final" ? "#4ADE80" : "#A3A3A3", borderColor: "currentColor" }}>{f.status === "final" ? "DEFINITIEF" : "CONCEPT"}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>

        {form && (
          <JaceAthleteForm athlete={athlete} onClose={() => setForm(false)} onSaved={() => { setForm(false); load(); toast.success(""); }} />
        )}
      </div>
    </JaceLayout>
  );
}
